import { useMemo } from 'react';

import { PermissionKeys } from 'src/auth/types';
import type { ActivityDTO } from 'src/types/dto';

import { useAuthContext } from 'src/auth/hooks';
import { useUserPermissions } from './use-user-permissions';

export type UpdateServicePermissions = {
  canUpdateAny: boolean;
  canUpdateOwn: boolean;
};

/**
 * GROUP/ALL: may update any activity. ACTIVITIES_UNDER_MY_PROJECTS: only activities on projects the user owns.
 */
export function useUpdateServicePermissions(): UpdateServicePermissions {
  const { hasAnyPermission } = useUserPermissions();

  return useMemo(
    () => ({
      canUpdateAny: hasAnyPermission([
        { permission: PermissionKeys.UPDATE_SERVICE, levels: ['GROUP', 'ALL'] },
      ]),
      canUpdateOwn: hasAnyPermission([
        { permission: PermissionKeys.UPDATE_SERVICE, levels: ['ACTIVITIES_UNDER_MY_PROJECTS'] },
      ]),
    }),
    [hasAnyPermission]
  );
}

export function canUpdateServiceForOwner(
  projectOwnerId: number | string | null | undefined,
  userId: number | string | null | undefined,
  { canUpdateAny, canUpdateOwn }: UpdateServicePermissions
): boolean {
  return (
    canUpdateAny ||
    (canUpdateOwn &&
      projectOwnerId != null &&
      userId != null &&
      Number(projectOwnerId) === Number(userId))
  );
}

/** Batch actions require a single project owner matching the current user when scoped to own projects. */
export function canUpdateServiceForActivities(
  activities: ActivityDTO[],
  userId: number | string | null | undefined,
  { canUpdateAny, canUpdateOwn }: UpdateServicePermissions
): boolean {
  if (canUpdateAny) return true;
  if (!canUpdateOwn || activities.length === 0) return false;

  const uniqueOwnerIds = Array.from(
    new Set(activities.map((activity) => activity.standard?.project?.owner?.id))
  );
  return (
    uniqueOwnerIds.length === 1 &&
    uniqueOwnerIds[0] != null &&
    userId != null &&
    Number(uniqueOwnerIds[0]) === Number(userId)
  );
}

/**
 * @deprecated Use useUpdateServicePermissions().canUpdateAny or canUpdateServiceForOwner / canUpdateServiceForActivities.
 */
export function useCanUpdateService(): boolean {
  const { canUpdateAny } = useUpdateServicePermissions();
  return canUpdateAny;
}

/**
 * Resolves update permission for the current user against one activity or a selection (e.g. plan box).
 */
export function useCanUpdateServiceSelection(activities: ActivityDTO[]): boolean {
  const { user } = useAuthContext();
  const permissions = useUpdateServicePermissions();

  return useMemo(
    () => canUpdateServiceForActivities(activities, user?.id, permissions),
    [activities, permissions, user?.id]
  );
}
